import * as _ from 'lodash';
import { useCallback, useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { FILE_PATH_IN_REPORTS, SNIPPET_DETECTION } from '../../../../shared/featureFlags/flagNames';
import {
  dependencyInfoOptionFormats,
  DependencyInfoOptions,
  dependencyInfoOptionsWithDescriptions,
  isReportFormat,
} from '../../../../shared/reports';
import { useParams } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useOrganizationPackageLabels } from '../../../hooks/api/packageLabels';
import useIsLicenseConcludedEnabled from '../../../hooks/features/useIsLicenseConcludedEnabled';
import { selectOptionFactory } from '../../../utils';
import FilterCardCheckboxes from '../../generic/Filter/FilterCardCheckboxes';
import FilterCardMultiSelect from '../../generic/Filter/FilterCardMultiSelect';
import FilterCardNestedCheckboxes from '../../generic/Filter/FilterCardNestedCheckboxes';
import FilterPanel from '../../generic/Filter/FilterPanel';
import {
  SavedOptionsContainer,
} from '../../projects/generic/ReportGenerationV2/shared/components/Options/SavedOptionsContainer';
import { licenseIncludeOptions } from '../../projects/generic/ReportGenerationV2/shared/constants';
import {
  dropOrphanedLicenseTextAppendix,
  isLicenseTextAppendixOption,
  nestLicenseTextAppendixOption,
} from '../../projects/generic/ReportGenerationV2/shared/licenseTextAppendixOption';
import { ReportOptionDetails } from '../../projects/generic/ReportGenerationV2/utils';
import { useCustomReportOptions } from '../hooks/useCustomReportOptions';
import { useDefaultSavedOptionId } from '../hooks/useDefaultSavedOptionId';
import { useReportFormatSelector } from '../hooks/useFormatSelector';
import { useReportType } from '../hooks/useReportType';
import { useSavedReportOptions } from '../hooks/useSavedReportOptions';
import { isReleaseGroupScope } from '../types';
import type { AttributionReportIncludeOptions, DependencyInfoOption } from '../../../../shared/reports';
import type { PartialReportConfig } from '../../../../shared/reports/types';
import type { SelectOptionWithAttributes } from '../../../types';
import type {
  SavedOptionApplySource,
} from '../../projects/generic/ReportGenerationV2/shared/components/Options/SavedOptions';
import type { ReportCategory } from '../../projects/generic/shared/types';
import type { ReportsV3Params, ReportType } from '../types';

/**
 * Maps V3 ReportType values to V2 category strings used in ReportOptionDetails.
 */
const reportTypeToCategoryMap: Record<ReportType, ReportCategory> = {
  licensing: 'licensing',
  sbom: 'sbom',
  vulnerability: 'vulnerability',
  remediationGuidance: 'remediation-guidance',
};

const labelOverrides: Partial<Record<string, string>> = {
  includeProjectLicense: 'Declared Licenses',
};

const toOption = (key: keyof typeof ReportOptionDetails): SelectOptionWithAttributes => {
  const { title, description } = ReportOptionDetails[key] ?? {};
  return {
    label: labelOverrides[key] ?? title ?? key,
    value: key,
    tooltip: description ? <div className="text-align-left">{description}</div> : undefined,
  };
};

const excludeLabeledDependenciesOption = toOption('excludeLabeledDependencies');

/**
 * Custom report options users will select to conditionally include or exclude data from reports.
 */
type CustomOptionsProps = {
  onSavedOptionsApply?: () => void;
};

export const CustomOptions: VoidFunctionComponent<CustomOptionsProps> = ({ onSavedOptionsApply }) => {
  const reportType = useReportType();
  const { value: formatOption } = useReportFormatSelector();
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);

  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(window?.user?.organizationId ?? 0);
  const isSnippetDetectionEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isFilePathInReportsEnabled = useIsFeatureEnabled(FILE_PATH_IN_REPORTS);

  const category = reportTypeToCategoryMap[reportType];
  const format = formatOption.value;

  const { flatOptions, licenseOptions } = useMemo(() => {
    const filtered = Object.values(ReportOptionDetails)
      .filter((detail) => {
        if (!detail) {
          return false;
        }
        const { key, format: applicableFormats, category: applicableCategories } = detail;

        if (key === 'includeConcludedLicenses' && !isLicenseConcludedEnabled) {
          return false;
        }
        if (key === 'includeFOSSADependencies' && !isReleaseGroup) {
          return false;
        }
        if (key === 'includeSnippetDependencies' && !isSnippetDetectionEnabled) {
          return false;
        }
        // Rendered as its own checkbox under the Exclude Package Labels select instead.
        if (key === 'excludeLabeledDependencies') {
          return false;
        }

        const isCorrectCategory = applicableCategories.includes(category);
        const isApplicableFormat = applicableFormats.has(format);
        return isCorrectCategory && isApplicableFormat;
      })
      .map(({ key }) => toOption(key));

    const [license, flat] = _.partition(filtered, (option) =>
      licenseIncludeOptions.has(option.value as keyof AttributionReportIncludeOptions),
    );

    return { flatOptions: flat, licenseOptions: license };
  }, [category, format, isLicenseConcludedEnabled, isReleaseGroup, isSnippetDetectionEnabled]);

  const dependencyMetadataOptions = useMemo(() => {
    return Object.entries(DependencyInfoOptions)
      .filter(([key]) => {
        if (!isReportFormat(format)) {
          return false;
        }
        if (key === 'Library' || key === 'NoticeFiles') {
          return false;
        }
        const applicableFormats = dependencyInfoOptionFormats[key as DependencyInfoOption];
        if (applicableFormats && !applicableFormats.has(format)) {
          return false;
        }
        if (key === 'FilePath') {
          return isFilePathInReportsEnabled;
        }
        if (key === 'Project') {
          return isReleaseGroup && reportType === 'licensing';
        }
        if (key === 'ConcludedLicense' && !isLicenseConcludedEnabled) {
          return false;
        }
        if (key === 'IssueNotes' || key === 'Source') {
          return reportType === 'licensing';
        }
        return true;
      })
      .map(([key, value]) => ({
        label: value,
        value: key,
        tooltip: (
          <div className="text-align-left">{dependencyInfoOptionsWithDescriptions[key as DependencyInfoOption]}</div>
        ),
      }));
  }, [format, isFilePathInReportsEnabled, isLicenseConcludedEnabled, isReleaseGroup, reportType]);

  /**
   * Fetch and handle package labels.
   */
  const { data: labelsResponse, isLoading: areLabelsLoading } = useOrganizationPackageLabels();
  const availableLabels = useMemo(
    () => (labelsResponse?.packageLabels ?? []).map((label) => selectOptionFactory(label.name)),
    [labelsResponse],
  );

  const flatOptionKeys = useMemo(() => flatOptions.map((o) => o.value), [flatOptions]);
  const licenseOptionKeys = useMemo(() => licenseOptions.map((o) => o.value), [licenseOptions]);

  // This hook manages the state for which custom report options are selected.
  // It uses the URL query params to store the selected options.
  const {
    includeValues,
    setIncludeOptions,
    licenseValues,
    setLicenseOptions,
    dependencyMetadataValues,
    setDependencyMetadata,
    excludePackageLabelValues,
    setExcludePackageLabels,
    isExcludingLabeledDependencies,
    setExcludeLabeledDependencies,
  } = useCustomReportOptions(flatOptionKeys, licenseOptionKeys);

  const { currentConfig, onApply, isDirty } = useSavedReportOptions();
  const initialOptionId = useDefaultSavedOptionId(reportType);

  const handleSavedOptionApply = useCallback(
    (config: PartialReportConfig | null, source?: SavedOptionApplySource) => {
      const didChange = onApply(config);
      if (didChange && (source === 'selection' || source === 'reset')) {
        onSavedOptionsApply?.();
      }
    },
    [onApply, onSavedOptionsApply],
  );

  const shouldShowExcludePackageLabels = reportType === 'licensing' || reportType === 'sbom';

  return (
    <FilterPanel title="Options">
      <SavedOptionsContainer
        currentConfig={currentConfig}
        handleSavedOptionApply={handleSavedOptionApply}
        initialOptionId={initialOptionId}
        isSavedOptionDirty={isDirty}
      />
      <div className="width-full">
        <FilterCardCheckboxes
          label="Included"
          options={flatOptions}
          values={includeValues}
          onChange={setIncludeOptions}
          tooltipPlacement="left"
        />
        {licenseOptions.length > 0 && (
          <FilterCardNestedCheckboxes
            options={licenseOptions}
            values={licenseValues}
            onChange={setLicenseOptions}
            parentCheckboxLabel="List of licenses"
            parentTooltip={
              <div className="text-align-left">
                A complete collection of all licenses detected across your project&apos;s dependencies.
              </div>
            }
            tooltipPlacement="left"
          />
        )}
      </div>
      {shouldShowExcludePackageLabels && (
        <>
          <FilterCardMultiSelect
            label="Exclude Package Labels"
            placeholder={areLabelsLoading ? 'Loading...' : 'Select one or more...'}
            options={availableLabels}
            values={excludePackageLabelValues}
            onChange={setExcludePackageLabels}
            isDisabled={availableLabels.length === 0 || isExcludingLabeledDependencies}
          />
          <FilterCardCheckboxes
            options={[excludeLabeledDependenciesOption]}
            values={isExcludingLabeledDependencies ? [excludeLabeledDependenciesOption] : []}
            onChange={(values) => setExcludeLabeledDependencies(values.length > 0)}
            tooltipPlacement="left"
          />
        </>
      )}
      <FilterCardCheckboxes
        label="Dependency Metadata"
        options={nestLicenseTextAppendixOption(dependencyMetadataOptions, dependencyMetadataValues)}
        values={dependencyMetadataValues}
        onChange={(next) => setDependencyMetadata(dropOrphanedLicenseTextAppendix(next))}
        tooltipPlacement="left"
        renderOption={(option, checkbox) =>
          isLicenseTextAppendixOption(option) ? <div className="padding-left-md width-full margin-bottom-xs">{checkbox}</div> : checkbox}
      />
    </FilterPanel>
  );
};
